import React, { useCallback, useEffect, useState, useRef } from 'react';
import Wizard, { WizardStep } from '../components/Wizard';
import UpdateComponentsStep from './Steps/UpdateComponentsStep';
import DownloadStep from './Steps/DownloadStep';
import UpdateAdminConsoleStep from './Steps/UpdateAdminConsoleStep';
import { Button, ButtonStyles, FloatingSnackbar, Icon, Icons, Popup, Toggle } from '@silverfort/sf-components';
import { DcComponent, NodeComponent, PushServiceComponent, SoftwareStatus } from '../types';
import ComponentsSideBar from './ComponentsSideBar';
import { useTowerLayout } from '../../../../TowerLayout/TowerLayoutProvider';
import { NODES_TYPES } from '../EnvironmentOverviewUtils';
import { handleDownloadLogs, handleSendLogs, initSelectedStep, isMinorVersionUpgrade } from './UpdateWizardUtils';
import { all } from 'axios';

const WizardStepsMap: { [key: string]: WizardStep } = {
    download: { title: 'Download', id: 'download' },
    adminConsole: { title: 'Admin Console', id: 'adminConsole' },
    pushServices: { title: 'Desktop Messaging Services', id: NODES_TYPES.PUSH_SERVICES },
    nodes: { title: 'Nodes', id: NODES_TYPES.NODES },
    dcs: { title: 'DCs', id: NODES_TYPES.DCS }
};

export interface UpdateWizardProps {
    softwareStatus: SoftwareStatus;
    serversData: {
        nodes: NodeComponent[];
        dcs: DcComponent[];
        pushServices: PushServiceComponent[];
    };
    onCancel: () => void;
    onFinish: () => void;
}

const UpdateWizard: React.FC<UpdateWizardProps> = ({ softwareStatus, serversData, onCancel, onFinish }) => {
    const [steps, setSteps] = useState<WizardStep[]>(Object.values(WizardStepsMap));
    const [selectedStep, setSelectedStep] = useState<number>(0);
    const [showLogs, setShowLogs] = useState<boolean>(false);
    const [showSnackbar, setShowSnackbar] = useState<boolean>(false);
    const [logsError, setLogsError] = useState<boolean>(false);
    const { isOfflineMode } = useTowerLayout();
    const [autoScroll, setAutoScroll] = useState<boolean>(true);
    const logsScrollPosition = useRef<number>(0);
    const logsContainerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        let initSteps = steps;
        if (serversData.pushServices.length === 0) {
            initSteps = [
                WizardStepsMap.download,
                WizardStepsMap.adminConsole,
                WizardStepsMap.nodes,
                WizardStepsMap.dcs
            ];

            setSteps(initSteps);
        }

        initSelectedStep({
            softwareStatus,
            serversData,
            isOfflineMode,
            steps: initSteps,
            setSelectedStep,
            WizardStepsMap
        });
    }, []);

    useEffect(() => {
        setSteps((prev) => {
            return prev.map((step, index) => ({
                ...step,
                isActive: index === selectedStep,
                isCompleted: index < selectedStep
            }));
        });
    }, [selectedStep]);

    const handleViewLogs = () => {
        setShowLogs(true);
    };

    const getStepComponent = useCallback(() => {
        const step = steps[selectedStep];

        switch (step.id) {
            case WizardStepsMap.download.id:
                return <DownloadStep softwareStatus={softwareStatus} />;
            case WizardStepsMap.adminConsole.id:
                return (
                    <UpdateAdminConsoleStep
                        handleSendLogs={() => handleSendLogs({ setLogsError, setShowSnackbar })}
                        softwareStatus={softwareStatus}
                    />
                );
            case WizardStepsMap.pushServices.id:
            case WizardStepsMap.nodes.id:
            case WizardStepsMap.dcs.id:
                return (
                    <UpdateComponentsStep
                        handleSendLogs={() => handleSendLogs({ setLogsError, setShowSnackbar })}
                        softwareStatus={softwareStatus}
                        components={serversData[step.id] || []}
                        componentsType={step.id}
                        allServersData={serversData}
                    />
                );
            default:
                return <DownloadStep softwareStatus={softwareStatus} />;
        }
    }, [selectedStep, steps, softwareStatus]);

    const isNextStepDisabled = useCallback((): boolean => {
        const step = steps[selectedStep];
        const isMinorUpgrade = isMinorVersionUpgrade(softwareStatus, serversData.dcs);
        const allowSkip = isMinorUpgrade && !softwareStatus.autoADAdapterUpgrade;
        const allNodesUpdated =
            !softwareStatus.upgradingNodes &&
            serversData.nodes.every((node) => node.version === softwareStatus.currentTowerVersion);
        const allPushServicesUpdated =
            !softwareStatus.upgradingPushServices &&
            serversData.pushServices.every((ps) => ps.version === softwareStatus.currentTowerVersion);
        const allProtectedDcsUpdated =
            !softwareStatus.upgradingDcs &&
            serversData.dcs
                .filter((dc) => dc.is_protected)
                .every((dc) => dc.version === softwareStatus.currentCastleVersion);

        switch (step.id) {
            case WizardStepsMap.download.id:
                if (isOfflineMode) return false;

                return (
                    softwareStatus.lastDownloadedVersion !== softwareStatus.maxTowerVersion ||
                    softwareStatus.downloading
                );
            case WizardStepsMap.adminConsole.id:
                if (isOfflineMode) return false;

                return !!(
                    softwareStatus.upgradingTower ||
                    softwareStatus.currentTowerVersion !== softwareStatus.maxTowerVersion ||
                    softwareStatus.towerUpgradeError
                );
            case WizardStepsMap.pushServices.id:
                return !allowSkip && !allPushServicesUpdated;
            case WizardStepsMap.nodes.id:
                return softwareStatus.upgradingNodes;
            case WizardStepsMap.dcs.id:
                return (
                    !allNodesUpdated ||
                    !allPushServicesUpdated ||
                    (!softwareStatus.autoADAdapterUpgrade && !allProtectedDcsUpdated)
                );
            default:
                return false;
        }
    }, [selectedStep, steps, serversData, softwareStatus]);

    const showComponentsSummary = useCallback(() => {
        const step = steps[selectedStep];
        return (
            step.id === WizardStepsMap.pushServices.id ||
            step.id === WizardStepsMap.nodes.id ||
            step.id === WizardStepsMap.dcs.id
        );
    }, [selectedStep, steps]);

    const ContentComponent = React.memo(function ContentComponent({ logs }: { logs: string[] }) {
        // Restore scroll position after logs change
        React.useLayoutEffect(() => {
            if (logsContainerRef.current) {
                if (autoScroll) {
                    logsContainerRef.current.scrollTop = logsContainerRef.current.scrollHeight;
                } else {
                    logsContainerRef.current.scrollTop = logsScrollPosition.current;
                }
            }
        }, [logs]);

        const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
            logsScrollPosition.current = (e.target as HTMLDivElement).scrollTop;
        };

        return (
            <div
                ref={logsContainerRef}
                onScroll={handleScroll}
                className="text-13 h-[450px] w-[1000px] flex flex-col gap-1 px-2 pt-2 pb-0 rounded-lg border border-sf-gray-100 bg-sf-gray-50 text-sf-gray-700 overflow-auto">
                {logs.map((log, index) => (
                    <div key={index} className="flex">
                        <span className="text-sf-gray-400 font-mono text-12 min-w-[30px]">{index + 1}</span> {log}
                    </div>
                ))}
            </div>
        );
    });

    const LogsSidebar = (
        <Wizard.Sidebar className="h-fit w-full">
            <div className="flex flex-col gap-1 font-sf">
                <div className="font-medium text-13 text-sf-gray-700 h-6 flex items-center">Actions</div>
                <div className="text-13 text-sf-gray-800">
                    {!isOfflineMode && (
                        <div
                            onClick={() => handleSendLogs({ setLogsError, setShowSnackbar })}
                            className="flex gap-2 h-8 items-center cursor-pointer active:bg-sf-gray-50 w-fit rounded-lg">
                            <Icon icon={Icons.CloudUpload} />
                            Send All Logs
                        </div>
                    )}
                    {steps[selectedStep]?.id !== WizardStepsMap.download.id && (
                        <div
                            onClick={handleViewLogs}
                            className="flex gap-2 h-8 items-center cursor-pointer active:bg-sf-gray-50 w-fit rounded-lg">
                            <Icon icon={Icons.EyeOn} />
                            View Upgrade Logs
                        </div>
                    )}
                </div>
            </div>
        </Wizard.Sidebar>
    );

    const LogsPopup = (
        <Popup
            open={showLogs}
            popupClassName="max-w-[1200px]"
            title="View Logs"
            ContentComponent={() => <ContentComponent logs={softwareStatus.logsData || []} />}
            FooterComponent={() => (
                <div className="flex justify-between w-full items-center gap-2">
                    <div className="flex gap-2 items-center">
                        <Toggle className="toggle" checked={autoScroll} onChange={setAutoScroll} />
                        <span className="font-sf text-13 text-sf-gray-700">Auto Scroll</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button
                            onClick={() => setShowLogs(false)}
                            text="Close"
                            buttonStyle={ButtonStyles.Tertiary}></Button>
                        <Button
                            onClick={() => handleDownloadLogs({ softwareStatus })}
                            text="Download Logs"
                            buttonStyle={ButtonStyles.PrimarySolid}></Button>
                    </div>
                </div>
            )}
            onClose={() => setShowLogs(false)}></Popup>
    );

    const isUpgradeInProgress =
        softwareStatus.upgradingTower ||
        softwareStatus.upgradingNodes ||
        (softwareStatus.upgradingDcs && !softwareStatus.autoADAdapterUpgrade) ||
        softwareStatus.upgradingPushServices;

    return (
        <>
            <Wizard>
                <Wizard.Steps steps={steps}></Wizard.Steps>
                <Wizard.Page
                    title={steps[selectedStep].title}
                    cancelButtonProps={{ onClick: onCancel, text: 'Close' }}
                    nextButtonProps={{
                        disabled: isNextStepDisabled() || isUpgradeInProgress,
                        text: selectedStep === steps.length - 1 ? 'Finish' : 'Next',
                        onClick: () =>
                            selectedStep === steps.length - 1 ? onFinish() : setSelectedStep((prev) => prev + 1)
                    }}
                    backButtonProps={
                        !selectedStep
                            ? undefined
                            : {
                                  onClick: () => setSelectedStep((prev) => (prev ? prev - 1 : 0)),
                                  disabled: isUpgradeInProgress
                              }
                    }>
                    {getStepComponent()}
                </Wizard.Page>
                <div className="flex flex-col gap-1 font-sf w-2/12">
                    {showComponentsSummary() && (
                        <ComponentsSideBar serversData={serversData} softwareStatus={softwareStatus} />
                    )}
                    {LogsSidebar}
                </div>
            </Wizard>
            {LogsPopup}
            <FloatingSnackbar
                className="mb-18 h-[40px] text-13"
                selections={0}
                open={showSnackbar}
                onClose={() => setShowSnackbar(false)}>
                <div className="text-sf-white">
                    {logsError ? 'Sending Silverfort logs failed.' : 'Sending Silverfort logs in background...'}
                </div>
            </FloatingSnackbar>
        </>
    );
};

export default UpdateWizard;
